import { Component, Inject, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {DateAdapter, ErrorStateMatcher } from '@angular/material/core';
import { FREE_PROJECTS, getCurrency, Project, PROJECT_DESCRIPTION_MAX, PROJECT_NAME_MAX, PROJECT_NAME_MIN } from '../../common/organisation';
import { UserAccessComponent } from '../users/user-access.component';
import { OrganisationService } from '../organisation.service';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

// a project can't be due to finish before it starts
const completionAfterStart = (group:AbstractControl):ValidationErrors | null => {
    const start = group.get('startDate')?.value
    const completion = group.get('completionDate')?.value
    return start && completion && new Date(completion) < new Date(start) ? { completionBeforeStart: true } : null
}

@Component({
    selector: 'project-dialog',
    styleUrls: ['./project-dialog.css'],
    templateUrl: 'project-dialog.html',
    standalone: false
})
export class ProjectDialogComponent {
    project:Project
    projectForm: FormGroup
    matcher = new MyErrorStateMatcher();
    title:string
    message:string = undefined
    PROJECT_NAME_MIN = PROJECT_NAME_MIN
    PROJECT_NAME_MAX = PROJECT_NAME_MAX
    PROJECT_DESCRIPTION_MAX = PROJECT_DESCRIPTION_MAX

    @ViewChild(UserAccessComponent) userAccess: UserAccessComponent

    constructor(public dialogRef: MatDialogRef<ProjectDialogComponent>, private formBuilder: FormBuilder, private organisationService:OrganisationService,
        @Inject(MAT_DIALOG_DATA) public data: {project:Project, numProjects:number, isNew:boolean, exempt:boolean}, private readonly adapter: DateAdapter<Date>) {

        this.adapter.setLocale("en-NZ");
        this.project = data.project

        this.projectForm = this.formBuilder.group({
            name: [this.project.name, [Validators.required, Validators.minLength(PROJECT_NAME_MIN), Validators.maxLength(PROJECT_NAME_MAX)]],
            description: [this.project.description, Validators.maxLength(PROJECT_DESCRIPTION_MAX)],
            startDate: [this.project.startDate || (data.isNew ? new Date() : undefined), Validators.required],
            completionDate: [this.project.completionDate],
            useOrganisationAccess: [this.project.useOrganisationAccess],
            sandbox: [{value:this.project.sandbox, disabled:!data.isNew}],
        }, { validators: completionAfterStart })

        this.title = data.isNew ? "Create project" : "Edit project"

        const currency = getCurrency(this.organisationService.organisation.currencyCode)

        if (data.isNew && !data.exempt) {
            if (data.numProjects === FREE_PROJECTS) {
                this.message = `This will take you over the freemium limit of ${FREE_PROJECTS} project${FREE_PROJECTS === 1 ? '' : 's'}. Each project above the limit at the end of the month is billed at ${currency.projectPrice}. Cancel if you wish to stay on freemium.`
            }
            if (data.numProjects > FREE_PROJECTS) {
                this.message = `Adding a project will increase your monthly bill by ${currency.projectPrice}.`
            }
        }
    }

    onCancelClick(): void {
        this.dialogRef.close();
    }

    // closes with an updated copy; the project itself only changes once it has been saved
    save() {
        this.projectForm.markAllAsTouched()
        const accessValid = this.projectForm.value.useOrganisationAccess || !this.userAccess || this.userAccess.isValid()
        if (!accessValid || this.projectForm.invalid) return

        const values = this.projectForm.getRawValue()
        const project:Project = {
            ...this.project,
            name: values.name.trim(),
            description: values.description || '',
            startDate: values.startDate,
            completionDate: values.completionDate || undefined,
            useOrganisationAccess: values.useOrganisationAccess,
            sandbox: values.sandbox,
        }
        if (this.userAccess) project.userAccess = this.userAccess.getUserAccess()

        this.dialogRef.close(project);
    }
}
